import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api, type AuditQuery } from '../api/client';
import { qk, useNameLookup } from '../api/hooks';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, QueryBoundary } from '../components/States';
import { useDebounced } from '../features/batch/ReviewPanel';
import { formatDateTime, shortId } from '../lib/format';

const ACTOR_LABEL = { USER: 'Nutzer', SYSTEM: 'System', SERVICE: 'Dienst' } as const;

function toIsoStart(d: string) {
  return d ? new Date(`${d}T00:00:00`).toISOString() : undefined;
}
function toIsoEnd(d: string) {
  return d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined;
}

export function AuditPage() {
  const names = useNameLookup();
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState<'' | 'USER' | 'SYSTEM' | 'SERVICE'>('');
  const [batchId, setBatchId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const dAction = useDebounced(action, 350);
  const dBatch = useDebounced(batchId, 350);
  const params: AuditQuery = {
    cursor: cursors.at(-1),
    limit: 25,
    action: dAction || undefined,
    actorType: actorType || undefined,
    batchId: dBatch || undefined,
    from: toIsoStart(from),
    to: toIsoEnd(to),
  };
  const q = useQuery({ queryKey: qk.audit(params), queryFn: () => api.auditEvents(params), placeholderData: keepPreviousData });
  const reset = () => setCursors([undefined]);
  const items = q.data?.items ?? [];

  return (
    <>
      <PageHeader eyebrow="Nachvollziehbarkeit" title="Protokoll" description="Unveränderliche Ereignisse zu Batches, Freigaben, Regeln und Administration. Es werden keine Geheimnisse protokolliert." />
      <Card>
        <Box sx={{ p: 2, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1.5fr 1fr 1fr' } }}>
          <TextField label="Aktion enthält" value={action} onChange={(e) => { setAction(e.target.value); reset(); }} placeholder="z. B. company_policy" />
          <TextField select label="Akteur" value={actorType} onChange={(e) => { setActorType(e.target.value as typeof actorType); reset(); }}>
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="USER">Nutzer</MenuItem>
            <MenuItem value="SYSTEM">System</MenuItem>
            <MenuItem value="SERVICE">Dienst</MenuItem>
          </TextField>
          <TextField label="Batch-ID" value={batchId} onChange={(e) => { setBatchId(e.target.value.trim()); reset(); }} />
          <TextField type="date" label="Von" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField type="date" label="Bis" value={to} onChange={(e) => { setTo(e.target.value); reset(); }} slotProps={{ inputLabel: { shrink: true } }} />
        </Box>
        <QueryBoundary query={q} action="Protokoll laden" loadingRows={8} what="das Protokoll">
          {items.length === 0 ? (
            <EmptyState title="Keine Ereignisse" description="Für diese Filter gibt es keine Protokolleinträge." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Protokoll" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Zeitpunkt</TableCell>
                    <TableCell>Akteur</TableCell>
                    <TableCell>Aktion</TableCell>
                    <TableCell>Bezug</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((e) => (
                    <TableRow key={e.id} data-testid="audit-row">
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</TableCell>
                      <TableCell>
                        {e.actorName ?? '–'}
                        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                          {ACTOR_LABEL[e.actorType]}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{e.action}</TableCell>
                      <TableCell>
                        {e.batchId && (
                          <Link component={RouterLink} to={`/batches/${e.batchId}`}>
                            Batch {shortId(e.batchId)}
                          </Link>
                        )}
                        {e.gesellschaftId && (
                          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                            {names.gesellschaften.get(e.gesellschaftId) ?? 'Gesellschaft'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 380 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                          {Object.keys(e.data).length ? JSON.stringify(e.data) : '–'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 'auto', alignSelf: 'center' }}>
            Seite {cursors.length}
          </Typography>
          <Button size="small" startIcon={<ChevronLeft size={16} />} disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
            Zurück
          </Button>
          <Button size="small" endIcon={<ChevronRight size={16} />} disabled={!q.data?.nextCursor} onClick={() => setCursors((c) => [...c, q.data!.nextCursor!])}>
            Weiter
          </Button>
        </Box>
      </Card>
    </>
  );
}
